import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grow from '@material-ui/core/Grow';
import IconButton from '@material-ui/core/IconButton';
import { setCustomUrl } from '@snowplow/browser-tracker';
import classNames from 'clsx';
import type { ReactNode } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';

import Translation from '@swf/components/Translation/Translation';
import CloseIcon from '@swf/core/assets/icons/cross.svg';
import { StatusCode } from '@swf/core/enums/statusCode';
import useIsMounted from '@swf/core/hooks/useIsMounted';
import useModal from '@swf/core/hooks/useModal';
import useTrackers from '@swf/core/hooks/useTrackers';
import useTrackingLabel from '@swf/core/hooks/useTrackingLabel';
import type { ClassNameProp, ValuesProp } from '@swf/core/types/Props';
import type { TrackedTranslation, TranslationTag } from '@swf/core/types/i18n';

import type { AlertVariant } from '../Alert/Alert';
import Alert from '../Alert/Alert';
import Button from '../Button/Button';

import {
  backdrop,
  background,
  buttons,
  celebration,
  closeButton as closeButtonClass,
  content,
  image,
  loading,
  paper,
  root,
  secondTitle,
  title,
  tourVideo,
  videoClass,
  videoInTop,
  videoTourContainer,
  videoTourPrompt
} from './Prompt.scss';

interface Text extends Partial<ValuesProp> {
  translation: TranslationTag;
}

interface TrackedText extends Partial<ValuesProp> {
  translation: TrackedTranslation;
}

interface TrackedButton extends Partial<ClassNameProp>, Partial<ValuesProp> {
  translation: TrackedTranslation;
  disabled?: boolean;
  href?: string;
}

export type Handler = () => void;

interface Props extends AlertVariant {
  alert?: ReactNode;
  classes?: {
    buttons?: string;
    title?: string;
    content?: string;
    paper?: string;
  };
  isExitPrompt?: boolean;
  linkButton?: TrackedButton;
  noCloseButton?: boolean;
  onClose?: Handler;
  onLinkButton?: Handler;
  onPrimaryButtonClick?: Handler;
  onSecondaryButtonClick?: Handler;
  primaryButton?: TrackedButton;
  primaryButtonVariant?: 'primary' | 'secondary';
  secondaryButton?: TrackedButton;
  secondaryTitle?: Text;
  showLinkButton?: boolean;
  title: TrackedText;
  video?: string;
  hasBackground?: boolean;
  isVideoTour?: boolean;
}

const Prompt: React.FC<Props> = ({
  alert,
  classes,
  children,
  isExitPrompt,
  linkButton,
  noCloseButton,
  onClose,
  onLinkButton,
  onPrimaryButtonClick,
  onSecondaryButtonClick,
  primaryButton,
  primaryButtonVariant = 'primary',
  secondaryButton,
  secondaryTitle,
  showLinkButton,
  title: { translation: titleTranslation, values: titleValues },
  hasBackground,
  video,
  variant,
  isVideoTour
}) => {
  const [visible, setVisible] = useState(true);
  const { addOnModalClose, removeOnModalClose, setIsPrompt } = useModal();
  const { pathname } = useLocation();
  const snowplowRef = useRef<string>('');
  const trackingTitle = useTrackingLabel(titleTranslation, titleValues);
  const { buttonPressed, pageView, errorViewed } = useTrackers();
  const isMounted = useIsMounted();

  const promptPathname =
    pathname + `/prompt/${trackingTitle && trackingTitle.toLowerCase().replace(/ /g, '-')}`;

  const hide = () => {
    setVisible(false);
    removeOnModalClose();

    if (onClose) {
      onClose();
    }
  };
  const hideAndCall = (handler?: Handler) => () => {
    hide();
    if (handler) {
      setCustomUrl(pathname);
      handler();
    }
  };

  useEffect(() => {
    if (!isExitPrompt && onClose && isMounted()) {
      addOnModalClose(onClose);
      setIsPrompt(true);
    }
  }, [addOnModalClose, isExitPrompt, isMounted, onClose, setIsPrompt]);

  useEffect(() => {
    if (visible && snowplowRef.current !== promptPathname) {
      snowplowRef.current = promptPathname;
      pageView(promptPathname);
      variant === 'error' && errorViewed({ errorCode: StatusCode.UNKNOWN as string });
    }
  }, [visible, pageView, errorViewed, promptPathname, variant]);

  return (
    <Dialog
      open={visible}
      classes={{
        root,
        container: isVideoTour ? videoTourContainer : '',
        paper: classNames(paper, classes?.paper, {
          [celebration]: variant === 'celebration',
          [loading]: variant === 'loading',
          [videoInTop]: video,
          [background]: hasBackground,
          [videoTourPrompt]: isVideoTour
        })
      }}
      TransitionComponent={Grow}
      BackdropProps={{ classes: { root: backdrop } }}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose?.();
        }
      }}
    >
      {variant === 'celebration' && <div className={image} />}
      {video && (
        <div className={classNames(videoClass, isVideoTour ? tourVideo : '')}>
          <video
            src={video}
            playsInline
            autoPlay
            muted
            loop={isVideoTour}
            data-testid="prompt-video"
          />
        </div>
      )}
      <DialogTitle classes={{ root: classNames(title, classes?.title) }}>
        <Translation {...titleTranslation} values={titleValues} />
        {!noCloseButton && (
          <IconButton
            classes={{ root: closeButtonClass }}
            onClick={() => {
              buttonPressed({ buttonName: 'Close' });
              hide();
              setCustomUrl(pathname);
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      {secondaryTitle && (
        <DialogTitle classes={{ root: secondTitle }}>
          <Translation {...secondaryTitle.translation} />
        </DialogTitle>
      )}

      <DialogContent classes={{ root: classNames(content, classes?.content) }}>
        {alert && <Alert variant={variant}>{alert}</Alert>}
        {children}
      </DialogContent>

      {primaryButton && (
        <div className={classNames(buttons, classes?.buttons)}>
          <Button
            variant={primaryButtonVariant}
            onClick={hideAndCall(onPrimaryButtonClick)}
            {...primaryButton}
          />
          {secondaryButton && (
            <Button
              variant="secondary"
              onClick={hideAndCall(onSecondaryButtonClick)}
              {...secondaryButton}
            />
          )}
          {linkButton && showLinkButton && (
            <Button variant="text" onClick={hideAndCall(onLinkButton)} {...linkButton} />
          )}
        </div>
      )}
    </Dialog>
  );
};

export default Prompt;
